import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RadioPrimeConfigValor {
  ativo: boolean;
  nome: string;
  url: string;
}

// Rádio Prime — INVISÍVEL e AUTÔNOMA. <audio> oculto tocando o stream.
// - Autoplay: tenta sozinho; se o browser bloquear (política de som), o 1º
//   clique/tecla de quem liga a TV libera e fica tocando a sessão inteira.
// - Auto-reconexão: rádio 24h cai; ao dar erro/stall/fim, recarrega e volta.
// - Som por tela: ?som=off (ou ?mute=1) silencia ESTA tela — use nas telas
//   secundárias lado a lado pra não dar eco (uma só carrega o som da sala).
export function RadioPrime() {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const mudo = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get('som') === 'off' || p.get('mute') === '1';
    } catch { return false; }
  })();

  const { data: cfg } = useQuery({
    queryKey: ['tv-radio-prime'],
    queryFn: async (): Promise<RadioPrimeConfigValor | null> => {
      const { data } = await supabase
        .from('configuracoes_sistema')
        .select('valor')
        .eq('chave', 'tv.radio_prime')
        .maybeSingle();
      const v = (data as any)?.valor;
      const parsed = typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return null; } })() : v;
      if (parsed && typeof parsed === 'object' && parsed.url) return parsed as RadioPrimeConfigValor;
      return null;
    },
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000, // trocar rádio / ligar-desligar reflete sem F5
  });

  useEffect(() => {
    const el = audioRef.current;
    if (!el || mudo || !cfg?.ativo || !cfg?.url) return;

    let vivo = true;
    let reconectar: ReturnType<typeof setTimeout> | undefined;

    // só tenta se estiver parado (play() em stream já tocando é no-op)
    const tocar = () => { if (el.paused) el.play().catch(() => {}); };

    // reconecta streams que caem (força novo fetch do mesmo mount)
    const onQueda = () => {
      if (!vivo) return;
      clearTimeout(reconectar);
      reconectar = setTimeout(() => {
        if (!vivo) return;
        el.src = `${cfg.url}${cfg.url.includes('?') ? '&' : '?'}_r=${Math.floor(performance.now())}`;
        el.load();
        tocar();
      }, 3000);
    };

    const onGesto = () => tocar();
    const onVisible = () => { if (document.visibilityState === 'visible') tocar(); };

    el.src = cfg.url;
    el.load();
    tocar(); // tenta autoplay imediato

    // Re-tenta sozinho a cada 4s: se o navegador estiver liberado pra autoplay
    // (flag/kiosque), ele ARRANCA sozinho no F5 sem ninguém tocar; se não, o
    // primeiro gesto abaixo destrava. Para quando engatar.
    const tentativa = setInterval(() => {
      if (!vivo) return;
      if (!el.paused) { clearInterval(tentativa); return; }
      tocar();
    }, 4000);

    // QUALQUER interação mínima libera o som (mexer o mouse, rolar, tecla…)
    const eventos = ['pointerdown', 'click', 'keydown', 'touchstart', 'touchend', 'mousemove', 'wheel', 'scroll'];
    eventos.forEach((e) => window.addEventListener(e, onGesto, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    el.addEventListener('error', onQueda);
    el.addEventListener('stalled', onQueda);
    el.addEventListener('ended', onQueda);

    return () => {
      vivo = false;
      clearTimeout(reconectar);
      clearInterval(tentativa);
      eventos.forEach((e) => window.removeEventListener(e, onGesto));
      document.removeEventListener('visibilitychange', onVisible);
      el.removeEventListener('error', onQueda);
      el.removeEventListener('stalled', onQueda);
      el.removeEventListener('ended', onQueda);
    };
  }, [cfg?.ativo, cfg?.url, mudo]);

  if (mudo || !cfg?.ativo || !cfg?.url) return null;

  return <audio ref={audioRef} preload="auto" style={{ display: 'none' }} />;
}
